import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable, unwrap, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { applyPermissionBasedFiltering } from '../../../components/ColumnManagement';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { FieldDefinition } from '../../../data-models/field-definition';
import { columnMetaToFieldDefinition } from '../../../data-models/field-definition/column-meta-mapping';
import { formulaFieldToFieldDefinition } from '../../../data-models/field-definition/formula-field-mapping';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { ViewModel } from '../../../models/view.model';
import { fetchColumns, fetchPageConfig, fetchViews } from '../../../services/queries/MaggieViewQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { AggFuncConfigs, ColumnMeta, MoicAggFuncConfig } from '../../../types';
import { formulaFieldsListLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import {
  formulaFieldEntityForView,
  formulaFieldPath,
  formulaFieldsForEntity,
} from '../../../util/formula-fields';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { PredefinedColumns } from '../components/OverviewTable/columns/PredefinedColumns/PredefinedColumns';
import { pageConfigPFJ } from './PageStateJ';

type ViewsValue = ViewModel[] | Promise<ViewModel[]>;

export const GCChartSelectedCompanyIdAtom = atom<number>(-1);

/**
 * Default fetches all portfolio views for the current user on first subscribe.
 * Consumers using `useAtomValue` will suspend on first read; use
 * {@link viewsPFLoadableJ} for non-suspending callers.
 */
export const viewsPFJ = atomWithDefault<ViewsValue>(async () => await fetchViews());

export const viewsPFLoadableJ = loadable(viewsPFJ);

/** Sync primitive atom; `-1` is the unselected sentinel. */
export const selectedViewIdPFJ = atom<number>(-1);

/**
 * Derived atom: reads {@link selectedViewIdPFJ} + {@link viewsPFJ} and returns
 * the matching view or `null`. Returns a Promise only while `viewsPFJ` is
 * still resolving its initial fetch; once resolved, reads are synchronous —
 * declaring this `async` would force every consumer to re-suspend on every
 * `set(viewsPFJ, ...)`, since an `async` atom yields a fresh Promise per
 * re-derivation (FD-2976).
 */
export const selectedViewPFJ = atom((get): ViewModel | null | Promise<ViewModel | null> => {
  const viewId = get(selectedViewIdPFJ);
  const views = get(viewsPFJ);
  if (views instanceof Promise) {
    return views.then((vs) => vs.find((view) => view.id === viewId) ?? null);
  }
  return views.find((view) => view.id === viewId) ?? null;
});

export const selectedViewPFLoadableJ = loadable(selectedViewPFJ);

/**
 * The column catalogue for a view type. Lazy and writable: it fetches on first read rather than
 * when the family member is created, and a frosted sample seeds it so nothing is requested.
 */
export const columnsMetaState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atomWithDefault(() => fetchColumns(companyViewType));
});

/**
 * FD-2976: primitive-returning indirection so dependents don't recompute on
 * every `viewsPFJ` write — `selectedViewPFJ` yields a fresh object reference
 * even for writes that only touch filteredFunds/gridState.
 */
const selectedViewCompanyViewTypeJ = atom((get) => {
  return get(unwrap(selectedViewPFJ))?.companyViewType;
});

/**
 * FieldDefinitions for the selected view's columns, keyed by `path || name`
 * (matching columnMetaToFieldDefinition's key rule and the lookup in
 * columnsMetaToColumnsDef). Scoped to the selected view's `companyViewType`, so
 * keys are unique within a viewType (FD-3271 was fixed by that scoping in
 * #6005). Empty Map until the view and its columns hydrate.
 */
export const currentViewFieldDefinitions = atom((get) => {
  const companyViewType = get(selectedViewCompanyViewTypeJ);
  const res = new Map<string, FieldDefinition>();

  if (!companyViewType) {
    return res;
  }

  const rawColumns = get(unwrap(columnsMetaState(companyViewType))) ?? ([] as ColumnMeta[]);
  rawColumns.reduce((acc, column) => {
    return acc.set(column.path || column.name, columnMetaToFieldDefinition(column));
  }, res);

  // Formula fields are FE-injected columns (not in the BE /column list); merge
  // their definitions so the colDef factory resolves their formatter like any
  // other column's. Scoped like useColumns' injection: values are only
  // materialized on the rows of the entity's own views.
  const formulaEntity = formulaFieldEntityForView(companyViewType);
  if (formulaEntity != null) {
    const formulaLoad = get(formulaFieldsListLoadable);
    const formulaFields = formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [];
    for (const field of formulaFieldsForEntity(formulaFields, formulaEntity)) {
      res.set(formulaFieldPath(field), formulaFieldToFieldDefinition(field));
    }
  }
  return res;
});

export const columnsByPathState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    return columnMeta.reduce((map, column) => {
      return map.set(column.path, column);
    }, new Map<string, ColumnMeta>());
  });
});

export const columnsByPathStateLoadable = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return loadable(columnsByPathState(companyViewType));
});

export const columnNameToPathState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    return columnMeta.reduce((map, column) => {
      return map.set(column.name, column.path);
    }, new Map<string, string>());
  });
});

export const columnNameToPathStateLoadable = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return loadable(columnNameToPathState(companyViewType));
});

export const columnNameToDisplayNameState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    const predefinedColumnsMeta = PredefinedColumns.getAllMeta(companyViewType);
    const columnsToHide = get(selectedColumnsGridDisplayFieldsOrgState);
    const filteredColumnMeta = applyPermissionBasedFiltering(columnMeta ?? [], columnsToHide ?? []);
    return [...filteredColumnMeta, ...predefinedColumnsMeta].reduce((map, column) => {
      return map.set(column.name, column.displayName);
    }, new Map<string, string>());
  });
});

export const aggFuncConfigState = atomFamily((companyViewType: COMPANY_VIEW_TYPE) => {
  return atom(async (get) => {
    const columnMeta = await get(columnsMetaState(companyViewType));
    const moic: Record<string, MoicAggFuncConfig> = {};

    for (const cm of columnMeta) {
      if (cm.aggFunc === 'moicGeneric' && cm.meta?.aggFuncParams?.moic) {
        moic[cm.path] = cm.meta.aggFuncParams.moic;
      }
    }

    return { moic } satisfies AggFuncConfigs;
  });
});

/**
 * Refetches portfolio views and page config and writes them to
 * {@link viewsPFJ} and {@link pageConfigPFJ}. Errors are surfaced via toast +
 * Sentry; the function never throws.
 */
export function useInvalidateViewState() {
  const { pushErrorToast } = useToastMessageState();
  return useAtomCallback(
    useCallback(
      async (_get, set) => {
        try {
          const views = await fetchViews();
          set(viewsPFJ, views);
          // page config takes a bit to update, so fetch after views instead of in Promise.all
          const pageConfig = await fetchPageConfig();
          set(pageConfigPFJ, pageConfig);
        } catch (err) {
          console.error(err);
          const message = getErrorMessage(err, 'Failed to refetch views');
          Sentry.logger.error(message);
          pushErrorToast({ message });
        }
      },
      [pushErrorToast]
    )
  );
}
